import { wrapIn } from "prosemirror-commands";
import { TextSelection, type Command } from "prosemirror-state";

import { findParentOfType, nodes } from "../schema";
import { isToggleOpen, setToggleOpen, toggleKey } from "../toggle-state";

/**
 * The five toggle items in the slash menu: wrap the current block in a toggle
 * whose title is either a paragraph or a heading of the given level
 * (`editor.md` §2).
 */
export function wrapInToggle(titleLevel: number | null = null): Command {
  return (state, dispatch, view) => {
    return wrapIn(nodes.toggle)(
      state,
      (tr) => {
        if (!dispatch) return;
        if (titleLevel !== null) {
          const mapped = tr.mapping.map(state.selection.from);
          const $pos = tr.doc.resolve(mapped);
          const found = findParentOfType($pos, nodes.toggle);
          if (found) {
            const title = found.node.firstChild;
            if (title && title.type !== nodes.heading) {
              tr.setNodeMarkup(found.pos + 1, nodes.heading, { level: titleLevel });
            }
          }
        }
        dispatch(tr.scrollIntoView());
      },
      view,
    );
  };
}

/** `Mod-Enter` inside a toggle: open or close it (a per-user preference). */
export function toggleOpenState(documentId: string | null): Command {
  return (state, dispatch) => {
    const found = findParentOfType(state.selection.$from, nodes.toggle);
    if (!found) return false;
    if (dispatch) {
      const key = toggleKey(found.node);
      setToggleOpen(documentId, key, !isToggleOpen(documentId, key));
    }
    return true;
  };
}

/** `Tab` in a toggle title moves into the body (§10). */
export const enterToggleBody: Command = (state, dispatch) => {
  const found = findParentOfType(state.selection.$from, nodes.toggle);
  if (!found) return false;
  const title = found.node.firstChild;
  if (!title) return false;
  const titleEnd = found.pos + 1 + title.nodeSize;
  if (state.selection.from > titleEnd) return false;
  if (found.node.childCount < 2) {
    // No body yet: make one, so Tab always has somewhere to go.
    const paragraph = nodes.paragraph.createAndFill();
    if (!paragraph) return false;
    if (dispatch) {
      const tr = state.tr.insert(titleEnd, paragraph);
      dispatch(tr.setSelection(TextSelection.near(tr.doc.resolve(titleEnd + 1))).scrollIntoView());
    }
    return true;
  }
  if (dispatch) {
    dispatch(
      state.tr.setSelection(TextSelection.near(state.doc.resolve(titleEnd + 1))).scrollIntoView(),
    );
  }
  return true;
};

/** `⇧-Tab` in a toggle body moves back to the title. */
export const leaveToggleBody: Command = (state, dispatch) => {
  const found = findParentOfType(state.selection.$from, nodes.toggle);
  if (!found) return false;
  const title = found.node.firstChild;
  if (!title) return false;
  const titleEnd = found.pos + 1 + title.nodeSize;
  if (state.selection.from <= titleEnd) return false;
  if (dispatch) {
    dispatch(
      state.tr
        .setSelection(TextSelection.near(state.doc.resolve(titleEnd - 1), -1))
        .scrollIntoView(),
    );
  }
  return true;
};
